export function formatCurrency(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return 'N/D';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency ?? 'BRL' }).format(value);
  } catch {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }
}

// Mostra só a moeda PRINCIPAL (BRL, "onde o padrão é real" — pedido
// explícito do proprietário, 2026-09-09) — nunca a lista inteira de moedas
// concatenada. Vendas em outra moeda continuam contando normalmente nos
// totais (nada muda no backend), só não aparecem aqui por padrão; quem
// quiser ver o detalhamento por moeda pede diretamente (não existe toggle
// nenhum na tela pra isso, de propósito — pedido simples, sem UI extra).
// Cai pra outra moeda só se não houver BRL nenhuma no período (ex.: só
// vendas em dólar) — nunca mostra 'N/D' tendo dado disponível.
export function formatPrimaryCurrency<T extends { currency: string }>(
  entries: T[] | null | undefined,
  amountKey: keyof T
): string {
  if (!entries || entries.length === 0) return 'N/D';
  const primary = entries.find((entry) => entry.currency === 'BRL') ?? entries[0];
  return formatCurrency(primary[amountKey] as unknown as number | null, primary.currency);
}

// Símbolo de exibição pro seletor de moeda (CurrencySelector.tsx) — só
// cosmético, nunca usado pra formatar valor (formatCurrency acima já usa
// Intl.NumberFormat com a moeda real). Moeda sem símbolo mapeado aqui
// aparece só com o código ISO (ex.: "CAD"), nunca quebra.
const CURRENCY_SYMBOLS: Record<string, string> = { BRL: 'R$', USD: 'US$', EUR: '€', GBP: '£' };

export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

// Acha o bucket de UMA moeda numa lista de totais por moeda (byCurrency) —
// usado pelos dashboards ao trocar a moeda selecionada. `undefined` quando
// a moeda não tem nenhuma linha na lista (ex.: período filtrado sem venda
// naquela moeda) — quem chama decide o placeholder (0 ou 'N/D').
export function pickCurrency<T extends { currency: string }>(
  entries: T[] | null | undefined,
  currency: string
): T | undefined {
  return entries?.find((entry) => entry.currency === currency);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return 'N/D';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/D';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'N/D';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/D';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// ISO 8601 (data local, início/fim do dia em UTC) — usado pelos filtros de
// período do painel. Mantido simples de propósito (sem lib de datas): o
// volume/precisão exigidos aqui não justificam uma dependência nova.
export function toIsoStartOfDay(date: Date): string {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

export function toIsoEndOfDay(date: Date): string {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}
